import { Vector2 } from 'three';
import { Areas, LogicalSpace, Obstacle, Polygon, Rectangle, Segment } from '@thive/fddm-types';
import { DEG2RAD, RAD2DEG } from 'three/src/math/MathUtils.js';

import {
  clampAngle180,
  getMiddlePoint,
  getVectorRotatedAroundPoint,
  rotatePoint,
} from '@/modules/common/helpers/math';
import { calculateDistance } from '@/helpers/utils';
import { areasToFlatCollection, isHighwayArea } from '@/modules/floorplanDesign/helpers';
import { angleBetweenVectors } from '@/modules/floorplanDesign/helpers/helpers';
import { ANGLE_EPSILON_LOOSE } from '@/helpers/angleConstants';

/**
 * @description mapping utilities for rectangles
 */
export class RectangleUtils {
  /**
   * @description expects an axis aligned rectangle defined by bottom left position, and converts it to a rectangle with center definition and length as the longest side
   * @example canvasLeftBottomRectToFDDMRect(10, 10, 10, 10, 0)
   * -> {
   *      centerX: 15,
   *      centerY: 15,
   *      width: 10,
   *      length: 10,
   *      angle: 0
   *  }
   */
  static canvasLeftBottomRectToFDDMRect = (
    x: number,
    y: number,
    width: number,
    length: number,
    angleInDeg: number,
  ): Rectangle => {
    const leftBottom = new Vector2(x, y);
    const unrotatedCenter = new Vector2(x, y).add(new Vector2(width / 2, length / 2));
    const center = getVectorRotatedAroundPoint(unrotatedCenter, leftBottom, angleInDeg);
    const shortestSide = Math.min(width, length);
    const longestSide = Math.max(width, length);
    const angleInRad =
      clampAngle180(
        shortestSide !== longestSide && width === shortestSide ? angleInDeg + 90 : angleInDeg,
      ) * DEG2RAD;

    return {
      centerX: center.x,
      centerY: center.y,
      width: Math.min(width, length),
      length: Math.max(width, length),
      angle: angleInRad,
    };
  };

  /**
   * @description expects rectangle defined by center position, and converts it to a rectangle with center definition and length as the longest side
   * @example canvasRectCenterToFDDMRect(10, 10, 10, 15, 0)
   * -> {
   *      centerX: 10,
   *      centerY: 10,
   *      width: 10,
   *      length: 15,
   *      angle: 0
   *  }
   */
  static canvasRectCenterToFDDMRect = (
    x: number,
    y: number,
    width: number,
    length: number,
    angleInDeg: number,
  ): Rectangle => {
    const shortestSide = Math.min(width, length);
    const longestSide = Math.max(width, length);
    const angleInRad =
      clampAngle180(
        shortestSide !== longestSide && width === shortestSide ? angleInDeg + 90 : angleInDeg,
      ) * DEG2RAD;

    return {
      centerX: x,
      centerY: y,
      width: Math.min(width, length),
      length: Math.max(width, length),
      angle: angleInRad,
    };
  };

  /**
   * @description expects a rectangle with length > width and angle expressed in radians, and converts it to an FDDM segment without id, height
   * @example toPartialSegment({
                centerX: 15,
                centerY: 15,
                width: 10,
                length: 20,
                angle: 0,
              })
   * -> 
              {
   *            start: {
                  x: 5,
                  y: 15,
                },
                end: {
                  x: 25,
                  y: 15,
                },
                leftSideWidth: 5,
                rightSideWidth: 5,
   *          }
   */
  static toPartialSegment = (rectangle: Rectangle): Omit<Segment, 'id' | 'height'> => {
    const center = new Vector2(rectangle.centerX, rectangle.centerY);
    const unrotatedStart = center.clone().setX(center.x - rectangle.length / 2);
    const start = getVectorRotatedAroundPoint(unrotatedStart, center, rectangle.angle * RAD2DEG);
    const unrotatedEnd = center.clone().setX(center.x + rectangle.length / 2);
    const end = getVectorRotatedAroundPoint(unrotatedEnd, center, rectangle.angle * RAD2DEG);

    return {
      start: {
        x: start.x,
        y: start.y,
      },
      end: {
        x: end.x,
        y: end.y,
      },
      leftSideWidth: rectangle.width / 2,
      rightSideWidth: rectangle.width / 2,
    };
  };

  /**
   * @description converts the 2D spatial signature of a segment into a rectangle.
   */
  static fromSegment = (segment: {
    start: Segment['start'];
    end: Segment['end'];
    leftSideWidth: Segment['leftSideWidth'];
    rightSideWidth: Segment['rightSideWidth'];
  }): Rectangle => {
    const { start, end, leftSideWidth, rightSideWidth } = segment;
    const thickness: number = leftSideWidth + rightSideWidth;
    const distance: number = calculateDistance(start, end);
    const center: Vector2 = getMiddlePoint(
      new Vector2(start.x, start.y),
      new Vector2(end.x, end.y),
    );
    const directionVector: { x: number; y: number } = {
      x: end.x - start.x,
      y: end.y - start.y,
    };

    return {
      centerX: center.x,
      centerY: center.y,
      width: Math.min(thickness, distance),
      length: Math.max(thickness, distance),
      angle:
        Math.atan2(directionVector.y, directionVector.x) + (thickness > distance ? Math.PI / 2 : 0),
    };
  };

  static polygonToRectangle(polygon: Polygon): Rectangle | undefined {
    const { points } = polygon;
    if (points.length !== 5) return undefined;

    // sides of the polygon
    const vectors: Vector2[] = [];
    for (let i = 0; i < points.length - 1; ++i) {
      vectors.push(
        new Vector2(points[i + 1].x, points[i + 1].y).sub(new Vector2(points[i].x, points[i].y)),
      );
    }

    // are opposite sides equal in length
    if (Math.abs(vectors[0].length() - vectors[2].length()) > ANGLE_EPSILON_LOOSE) return undefined;
    if (Math.abs(vectors[1].length() - vectors[3].length()) > ANGLE_EPSILON_LOOSE) return undefined;

    // does the polygon have 90 degree corners
    if (Math.abs(vectors[0].dot(vectors[1])) > ANGLE_EPSILON_LOOSE) return undefined;
    if (Math.abs(vectors[2].dot(vectors[3])) > ANGLE_EPSILON_LOOSE) return undefined;

    const center = getMiddlePoint(
      new Vector2(points[0].x, points[0].y),
      new Vector2(points[2].x, points[2].y),
    );

    return {
      centerX: center.x,
      centerY: center.y,
      width: vectors[0].length() > vectors[1].length() ? vectors[1].length() : vectors[0].length(),
      length: vectors[0].length() > vectors[1].length() ? vectors[0].length() : vectors[1].length(),
      angle:
        vectors[0].length() > vectors[1].length()
          ? -angleBetweenVectors(vectors[0], new Vector2(1, 0))
          : -angleBetweenVectors(vectors[1], new Vector2(1, 0)),
    };
  }

  static rectangleToPolygon(rect: Rectangle): Vector2[] {
    const { centerX, centerY, width, length, angle } = rect;
    const hWidth = length / 2;
    const hHeight = width / 2;

    const p1 = rotatePoint(centerX - hWidth, centerY - hHeight, centerX, centerY, angle);
    return [
      p1,
      rotatePoint(centerX - hWidth, centerY + hHeight, centerX, centerY, angle),
      rotatePoint(centerX + hWidth, centerY + hHeight, centerX, centerY, angle),
      rotatePoint(centerX + hWidth, centerY - hHeight, centerX, centerY, angle),
      p1,
    ].map((p) => new Vector2(p.x, p.y));
  }

  static fddmRectToCanvasLeftBottomRect = (rectangle: Rectangle) => {
    const angleInDeg = rectangle.angle * RAD2DEG;
    const center = new Vector2(rectangle.centerX, rectangle.centerY);
    const unrotatedLeftBottom = center
      .clone()
      .sub(new Vector2(rectangle.length / 2, rectangle.width / 2));
    const bottomLeft = getVectorRotatedAroundPoint(unrotatedLeftBottom, center, angleInDeg);

    return {
      x: bottomLeft.x,
      y: bottomLeft.y,
      width: rectangle.width,
      length: rectangle.length,
      angleInDeg,
    };
  };

  /**
   * given a collection of areas, return their rectangles, and convert highway area segments to rectangles
   */
  static convertAreasToRectangles(areas: Areas): Rectangle[] {
    const areaCollection = areasToFlatCollection(areas);

    return areaCollection.flatMap((item) => {
      if (isHighwayArea(item)) {
        const { segments } = item;
        return segments.flatMap(this.fromSegment);
      }

      return item.rectangle;
    });
  }

  /**
   * given a collection of obstacles, return their rectangles
   */
  static convertObstaclesToRectangles(obstacles: Obstacle[]): Rectangle[] {
    return obstacles.flatMap((item) => {
      const { segments } = item;
      return segments.flatMap(this.fromSegment);
    });
  }

  /**
   * @description converts logical spaces to rectangles based on their polygon data
   */
  static convertLogicalSpacesToRectangles(logicalSpaces: LogicalSpace[]): Rectangle[] {
    return logicalSpaces.flatMap((logicalSpace) => {
      const { polygon } = logicalSpace;
      const rectangle = this.polygonToRectangle(polygon);

      return (rectangle && [rectangle]) || [];
    });
  }
}
